import {
  buildColorAccessor,
  buildMeasureAccessor,
  computeMetricComparison,
  DataPointsApiResponse,
  DataRow,
  getLatestValue,
  getMeasureFormatString,
  MetricBlue,
  MetricsNeutralColor
} from '@tableau/metrics-engine';
import { MetricValuePill } from '@tableau/metrics-shared-widgets-dom';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import { connect } from 'react-redux';

import { AppState } from '../../app/state/AppState';
import { MapStateToProps } from '../../app/utils/ConnectUtils';
import { CommonStyles } from '../../common/react/CommonStyles';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IExplorableItemTypeInfo } from '../../ts/models/resourceInfoParts';
import { getMetricMeasure, getTableWithDefaultComparison } from '..';
import { isMetricInfo } from '../models/IMetricInfo';
import { selectMetricDataForContentItem } from '../selectors';

const containerCellStyle: React.CSSProperties = {
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'row',
  paddingTop: CommonStyles.Sizes.MediumSpace,
  alignItems: 'center',
  justifyContent: 'center',
  width: CommonStyles.Sizes.MetricValueCellWidth,
  height: CommonStyles.Sizes.RowHeight,
};

// we have a connected metric value pill so that as metric data comes in and updates, we only re-render the pill,
// not the whole data grid. This is tested in test\src\ts\app\spaces\ListView\DataGridCellsTests.tsx
export interface MetricValuePillWrapperStateProps {
  readonly metricData?: DataPointsApiResponse;
  readonly measureAccessor?: (point: DataRow) => number;
  readonly colorAccessor?: (point: DataRow) => string;
}

export interface MetricValuePillWrapperOwnProps {
  readonly row: DataGrid.Row;
}

export type MetricValuePillWrapperProps = MetricValuePillWrapperStateProps & MetricValuePillWrapperOwnProps;

export class MetricValuePillWrapper extends React.PureComponent<MetricValuePillWrapperProps> {
  public render() {
    const rowData = this.props.row.data as IExplorableItemTypeInfo;
    if (!isMetricInfo(rowData) || !FeatureFlags.metricsSpaceEnabled) {
      // render blank cell for non-metrics. This happens in mixed content pages.
      return null;
    }

    let measure;
    let format;

    if (this.props.metricData && this.props.metricData.data && this.props.metricData.data.length > 0) {
      measure = this.props.measureAccessor && getMetricMeasure(this.props.metricData, this.props.measureAccessor);
      format = this.props.metricData && getMeasureFormatString(this.props.metricData.definition);
    }

    const comparisonTable = this.props.metricData && getTableWithDefaultComparison(this.props.metricData);

    const latestValue = comparisonTable && getLatestValue(comparisonTable);
    const comparison = latestValue && computeMetricComparison(latestValue);

    const colorsEnabledColor =
      latestValue && comparison
        ? this.props.colorAccessor(comparison.comparedPoint)
        : MetricsNeutralColor;

    const color = FeatureFlags.metricsColorIndicatorsEnabled ? colorsEnabledColor : MetricBlue;

    return (
      <span style={containerCellStyle}>
        <MetricValuePill
          value={measure}
          measureFormat={format}
          color={color}
          colorIndicatorsEnabled={FeatureFlags.metricsColorIndicatorsEnabled}
          leftAlign={true}
        />
      </span>
    );
  }
}

const mapStateToProps: MapStateToProps<MetricValuePillWrapperStateProps> = (state: AppState, ownProps: MetricValuePillWrapperOwnProps) => {
  const metricData = ownProps.row.data ? selectMetricDataForContentItem(state, ownProps.row.data) : undefined;

  return {
    metricData,
    measureAccessor: metricData ? buildMeasureAccessor(metricData.definition) : undefined,
    colorAccessor: metricData ? buildColorAccessor(metricData.definition) : undefined
  };
};

export const ConnectedMetricValuePillWrapper = connect(mapStateToProps)(MetricValuePillWrapper);
